import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ListboxModule } from 'primeng/listbox';
import { BadgeModule } from 'primeng/badge';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { InputTextModule } from 'primeng/inputtext';
import { DividerModule } from 'primeng/divider';
import { TooltipModule } from 'primeng/tooltip';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { Conversation, MessageInterface, ConversationStatus, TransferHistoryItem } from '../../../models/chat';
import { CustomerConversationService } from '../../../services/customer-conversation.service';
import { AuthorizationService } from '../../../services/authorization.service';
import { AgentService } from '../../core/agent/services/agent.service';
import { CampaignService } from '../../../services/campaign.service';
import Swal from 'sweetalert2';
import { ChatNotificationService } from '../../../services/chat-notification.service';
import { Agent, UserRole } from '../../../models/users';
import { SharedDataService } from '../../../services/shared-data.service';
import { ConversationHistoryService, TranscriptMessage } from './conversation-history.service';
import { Ripple } from 'primeng/ripple';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

const roleTranslationKey: Record<UserRole, string> = {
  agent: 'agent.list.roleAgent',
  supervisor: 'agent.list.roleSupervisor',
  admin: 'agent.list.roleAdmin',
}

@Component({
  selector: 'app-chat-list-panel',
  standalone: true,
  imports: [
    CommonModule,
    ListboxModule,
    BadgeModule,
    FormsModule,
    InputTextModule,
    DividerModule,
    TooltipModule,
    OverlayBadgeModule,
    DialogModule,
    ButtonModule,
    Ripple,
    TranslateModule,
  ],
  templateUrl: './chat-list-panel.component.html',
  styleUrls: ['./chat-list-panel.component.scss'],
})
export class ChatListPanelComponent implements OnInit, OnChanges, OnDestroy {
  @Input() recentConversation: Conversation[] = [];
  @Input() selectedConversation: Conversation | null = null;
  @Input() loading: boolean = false;
  @Output() conversationUnavailable = new EventEmitter<string>();
  public activeTab: 'available' | 'assigned' = 'available';

  // Pagination & History
  showHistoryModal = false;
  finishedConversations: Conversation[] = [];
  totalHistory = 0;
  pageIndex = 0;
  pageSize = 10;
  loadingHistory = false;
  selectedHistoryConversation: Conversation | null = null;
  transcript: TranscriptMessage[] = [];
  loadingTranscript = false;

  activeConversationIds = new Set<string>();
  activeConversations: Conversation[] = [];
  // inactiveConversations: Conversation[] = [];
  filteredConversations: Conversation[] = [];
  searchQuery = '';
  status_available = ConversationStatus;
  user: Agent | null = null;
  isSupervisor = false;
  chatsWithNewMessage: Record<string, boolean> = {};
  private subs: any[] = [];
  public avgWaitTime: number = 0;
  public pendingConversations: Conversation[] = [];

  get panelTitle(): string {
    const role = this.user?.role || 'agent';
    const roleName = this.translate.instant(roleTranslationKey[role]);
    return this.translate.instant('agent.list.panelTitle', { role: roleName });
  }

  constructor(
    private customerConversationService: CustomerConversationService,
    private agentService: AgentService,
    private authService: AuthorizationService,
    private campaignService: CampaignService,
    private sharedDataService: SharedDataService,
    private chatNotificationService: ChatNotificationService,
    private historyService: ConversationHistoryService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.user = this.sharedDataService.getAgent();
    this.isSupervisor = this.user?.role === 'supervisor';

    this.filteredConversations = [...this.recentConversation];
    this.sortConversations();

    this.subs.push(
      this.chatNotificationService.getNewMessageMap$().subscribe(map => {
        this.chatsWithNewMessage = map;
        this.sortConversations();
      })
    );

    this.user = this.sharedDataService.getAgent();
    this.loadActiveConversations();
    this.splitConversations();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['recentConversation']) {
      this.filteredConversations = [...this.recentConversation];
      this.searchQuery ? this.filterConversations() : this.sortConversations();
      this.loadActiveConversations();
      this.splitConversations();
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  // ---------------- History & Pagination ----------------

  openHistory(): void {
    this.showHistoryModal = true;
    this.pageIndex = 0;
    this.selectedHistoryConversation = null;
    this.transcript = [];
    this.loadFinishedConversations();
  }

  loadFinishedConversations(): void {
    this.loadingHistory = true;
    const skip = this.pageIndex * this.pageSize;
    const limit = this.pageSize;

    const stored = localStorage.getItem('logged_in_agent');
    if (!stored) {
      console.error('No hay agente en localStorage');
      this.loadingHistory = false;
      return;
    }
    const agent = JSON.parse(stored) as { id: string };
    const agentId = agent.id;

    this.historyService
      .getFinishedConversations(agentId, skip, limit)
      .subscribe({
        next: (convs) => {

          // sort conversation bt end date
          convs.sort((a, b) => {
            const dateA = new Date(a.endDate || a.startDate);
            const dateB = new Date(b.endDate || b.startDate);
            return dateB.getTime() - dateA.getTime();
          });

          this.finishedConversations = convs;
          this.totalHistory = convs.length;
          this.loadingHistory = false;
        },
        error: err => {
          console.error('Error cargando historial', err);
          this.loadingHistory = false;
        }
      });
  }
  copyTranscriptToClipboard(): void {
    if (!this.transcript?.length || !this.selectedHistoryConversation) {
      return;
    }

    const vars = this.selectedHistoryConversation.customVariables || {};
    const userFullName = [
      vars['Nombres'],
      vars['Apellidos']
    ].filter(Boolean).join(' ') || this.translate.instant('agent.list.client');

    const agentName = this.translate.instant('agent.list.agent');

    const text = this.transcript
      .sort((a, b) =>
        new Date(a.datePending).getTime() - new Date(b.datePending).getTime()
      )
      .map(msg => {

        // date pending example 2025-08-05T08:41:38.957Z
        console.log('msg.datePending', msg.datePending, typeof msg.datePending);
        // Use UTC methods to preserve the original timezone
        const d = new Date(msg.datePending);
        const utcHours = d.getUTCHours();
        const mm = d.getUTCMinutes().toString().padStart(2, '0');

        // Convert to 12-hour format with AM/PM
        const isPM = utcHours >= 12;
        const displayHours = utcHours === 0 ? 12 : utcHours > 12 ? utcHours - 12 : utcHours;
        const hh = displayHours.toString().padStart(2, '0');
        const ampm = isPM ? 'PM' : 'AM';

        const who = msg.type === 'agent' ? agentName : userFullName;
        return `[${hh}:${mm} ${ampm}] ${who}: ${msg.content}`;
      })
      .join('\n');

    navigator.clipboard.writeText(text)
      .then(() => {
        console.log('✅ Historial copiado al portapapeles');
      })
      .catch(err => {
        console.error('❌ No se pudo copiar el historial:', err);
      });
  }

  prevPage(): void {
    if (this.pageIndex > 0) {
      this.pageIndex--;
      this.loadFinishedConversations();
    }
  }

  nextPage(): void {
    if ((this.pageIndex + 1) * this.pageSize < this.totalHistory) {
      this.pageIndex++;
      this.loadFinishedConversations();
    }
  }

  selectHistory(conv: Conversation): void {
    this.selectedHistoryConversation = conv;
    this.loadingTranscript = true;
    this.historyService.getTranscript(conv.conversationId!)
      .subscribe({
        next: msgs => {
          this.transcript = msgs;
          this.loadingTranscript = false;
        },
        error: err => {
          console.error('Error cargando transcripción', err);
          this.transcript = [];
          this.loadingTranscript = false;
        }
      });
  }

  private loadActiveConversations(): void {
    const agent = this.user;

    if (this.isSupervisor) {
      // For supervisors: fetch campaigns for current workspace and filter conversations by those campaigns
      const raw = sessionStorage.getItem('workpsaceId');
      if (!raw) {
        // Fail-closed for supervisors: never load unscoped conversations.
        this.recentConversation = [];
        this.filteredConversations = [];
        this.splitConversations();
        return;
      }

      let workspace: any;
      try {
        workspace = JSON.parse(raw);
      } catch (e) {
        console.error('Workspace inválido en sessionStorage (workpsaceId)', e);
        this.recentConversation = [];
        this.filteredConversations = [];
        this.splitConversations();
        return;
      }

      const workspaceId = String(workspace?.id || workspace?.workspaceId || '');
      const platformId = Number((workspace?.companyPlatformId ?? workspace?.company_platform_id ?? workspace?.platformId) || 0);

      if (!workspaceId) {
        this.recentConversation = [];
        this.filteredConversations = [];
        this.splitConversations();
        return;
      }

      // Get campaigns for workspace, then filter conversations by those campaign ids
      this.subs.push(this.campaignService.getCampaignsByWorkspaceId(workspaceId).subscribe({
        next: (campaigns) => {
          const campaignIds = (campaigns || []).map(c => c.id).filter(Boolean) as string[];
          this.subs.push(this.customerConversationService.getConversationAvailableAgent(agent?.id || '').subscribe({
            next: (conversations: Conversation[]) => {
              const filtered = (conversations || []).filter(c => campaignIds.includes(c.campaignId));
              this.recentConversation = filtered;
              this.filteredConversations = [...this.recentConversation];
              this.splitConversations();
            },
            error: err => {
              console.error('Error cargando conversaciones para supervisor:', err);
            }
          }));
        },
        error: err => {
          console.error('Error cargando campañas del workspace en ChatListPanel:', err);
          this.recentConversation = [];
          this.filteredConversations = [];
          this.splitConversations();
        }
      }));
      return;
    }
    if (!agent?.id) return;
    this.customerConversationService.getActiveConversations(agent.id).subscribe({
      next: convs => {
        this.activeConversationIds = new Set(convs.map(c => c.conversationId!));
        localStorage.setItem('act_cvns', JSON.stringify(convs));
      },
      complete: () => {
        this.splitConversations();

      },
      error: err => console.error('Error cargando activas:', err)
    });
  }

  private splitConversations(): void {
    const all = this.filteredConversations;

    this.activeConversations = all.filter(c =>
      c.status === ConversationStatus.Active && this.belongsToCurrentAgentList(c)
    );
    this.pendingConversations = all.filter(c =>
      c.status === ConversationStatus.Pending && this.belongsToCurrentAgentList(c)
    );

  }

  private belongsToCurrentAgentList(conversation: Conversation): boolean {
    if (this.isSupervisor) {
      return true;
    }

    const currentAgentId = String(this.user?.id ?? '').trim();
    if (!currentAgentId) {
      return false;
    }

    if (this.isActiveConversation(conversation)) {
      return String(conversation.agentId ?? '').trim() === currentAgentId;
    }

    if (conversation.status === ConversationStatus.Pending) {
      const allowedCampaigns = Array.isArray(this.user?.campaigns)
        ? this.user.campaigns.map(String)
        : [];
      return !!conversation.campaignId && allowedCampaigns.includes(String(conversation.campaignId));
    }

    return true;
  }

  sortConversations(): void {
    const statusOrder = {
      [ConversationStatus.Pending]: 1,
      [ConversationStatus.Active]: 2,
      [ConversationStatus.Finish]: 3
    };
    this.filteredConversations.sort((a, b) => {
      const aHasNotif = this.hasNewMessage(a.conversationId!) ? 0 : 1;
      const bHasNotif = this.hasNewMessage(b.conversationId!) ? 0 : 1;
      if (aHasNotif !== bHasNotif) return aHasNotif - bHasNotif;
      const orderA = statusOrder[a.status] || 99;
      const orderB = statusOrder[b.status] || 99;
      if (orderA !== orderB) return orderA - orderB;
      const dateA = a.status === ConversationStatus.Active && a.acceptedDate ? a.acceptedDate : a.startDate;
      const dateB = b.status === ConversationStatus.Active && b.acceptedDate ? b.acceptedDate : b.startDate;
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });
  }

  onChatSelect(conversation: Conversation): void {
    const conversationId = conversation?.conversationId;
    if (!conversationId) {
      return;
    }

    this.customerConversationService.getConversationById(conversationId).subscribe({
      next: (currentConversation) => {
        if (String(currentConversation?.status ?? '').toLowerCase() === 'finish') {
          this.handleUnavailableConversation(conversationId, 'La conversación ya fue cerrada');
          return;
        }

        const validatedConversation = this.normalizeConversationForSelection(conversation, currentConversation);
        if (this.isActiveConversationAssignedToAnotherAgent(validatedConversation)) {
          this.handleUnavailableConversation(
            conversationId,
            'La conversación fue asignada a otro agente'
          );
          return;
        }

        this.continueConversationSelection(validatedConversation);
      },
      error: (error) => {
        if (error?.status === 404) {
          this.handleUnavailableConversation(conversationId, 'La conversación ya no está disponible');
          return;
        }

        console.error('Error validating conversation before selection:', error);
        this.loadActiveConversations();
      }
    });
  }

  private continueConversationSelection(conversation: Conversation): void {
    if (this.isActiveConversationAssignedToAnotherAgent(conversation)) {
      this.handleUnavailableConversation(
        conversation.conversationId!,
        'La conversación fue asignada a otro agente'
      );
      return;
    }

    this.loadActiveConversations();
    const ratio = localStorage.getItem('rt') ?? '1:3';
    const accept_conversation = localStorage.getItem('accept_conversation');
    const parts = ratio!.split(':');
    const n = parseInt(parts[1], 10);

    if (this.activeConversationIds.has(conversation.conversationId!) || this.activeConversationIds.size < n && accept_conversation == 'true') {
      this.chatNotificationService.clearNewMessage(conversation.conversationId!);

      const agent = JSON.parse(localStorage.getItem('logged_in_agent')!)
      const body = {
        conversationId: conversation.conversationId,
        agentId: agent.id,
        status: "Active"
      };

      if (this.user?.role === 'agent') {
        const token = localStorage.getItem('auth_req');
        if (!token) {
          console.error('❌ No se encontró token en localStorage (auth_req)');
          this.loadActiveConversations();
          return;
        }

        this.agentService
          .isConversationAlreadySelected(conversation.conversationId, token)
          .subscribe({
            next: (result: any) => {
              if (result.agentId) {
                if (result.agentId === agent.id) {
                  // Ya eres tú quien tiene la conversación
                  this.loadActiveConversations();
                  // Notify parent component about conversation selection
                  this.notifyConversationSelected(conversation);
                } else {
                  // Otro agente la tomó
                  Swal.fire({
                    allowEscapeKey: false,
                    title: this.translate.instant('agent.list.alertAssignedTitle'),
                    text: this.translate.instant('agent.list.alertAssignedText'),
                    icon: 'info',
                    iconColor: '#000000',
                    confirmButtonText: this.translate.instant('agent.common.understood'),
                    confirmButtonColor: '#000000',
                    background: '#f8f9fa',
                    color: '#212529',
                    customClass: {
                      popup: 'rounded-3 shadow',
                      confirmButton: 'px-4 py-2',
                    }
                  }).then(() => {
                    this.conversationUnavailable.emit(conversation.conversationId!);
                    this.loadActiveConversations();
                  });
                }
              } else {
                // Nadie la tiene, tú la tomas
                this.agentService
                  .setAgentConversation(body, token)
                  .subscribe({
                    complete: () => {
                      conversation.status = ConversationStatus.Active;
                      this.sortConversations();
                      this.loadActiveConversations();
                      // Notify parent component about conversation selection
                      this.notifyConversationSelected(conversation);
                    },
                    error: () => {
                      this.loadActiveConversations();
                    }
                  });
              }
            },
            error: () => {
              this.loadActiveConversations();
            }
          });
      } else {
        // For supervisors or other roles, directly notify the selection
        this.notifyConversationSelected(conversation);
      }

    } else if (this.activeConversationIds.size >= n) {
      Swal.fire({
        allowEscapeKey: false,
        title: this.translate.instant('agent.list.alertLimitTitle'),
        icon: 'info',
        iconColor: '#000000',
        confirmButtonText: this.translate.instant('agent.common.understood'),
        confirmButtonColor: '#000000',
        background: '#f8f9fa',
        color: '#212529',
        customClass: {
          popup: 'rounded-3 shadow',
          confirmButton: 'px-4 py-2',
        }
      });
      return;
    }
  }

  private handleUnavailableConversation(conversationId: string, message: string): void {
    this.recentConversation = this.recentConversation.filter(
      conversation => conversation.conversationId !== conversationId
    );
    this.filteredConversations = this.filteredConversations.filter(
      conversation => conversation.conversationId !== conversationId
    );
    this.activeConversations = this.activeConversations.filter(
      conversation => conversation.conversationId !== conversationId
    );
    this.pendingConversations = this.pendingConversations.filter(
      conversation => conversation.conversationId !== conversationId
    );
    this.activeConversationIds.delete(conversationId);

    if (this.selectedConversation?.conversationId === conversationId) {
      this.selectedConversation = null;
    }

    this.chatNotificationService.clearNewMessage(conversationId);
    this.customerConversationService.resetConversation();
    this.conversationUnavailable.emit(conversationId);
    this.splitConversations();
    this.loadActiveConversations();

    Swal.fire({
      title: message,
      icon: 'info',
      confirmButtonText: this.translate.instant('agent.common.understood'),
      confirmButtonColor: '#000000',
      background: '#f8f9fa',
      color: '#212529'
    });
  }

  private normalizeConversationForSelection(
    listedConversation: Conversation,
    currentConversation: Conversation | Record<string, unknown>
  ): Conversation {
    const current = currentConversation as Record<string, unknown>;

    return {
      ...listedConversation,
      ...currentConversation,
      conversationId: String(current['conversationId'] ?? current['conversation_id'] ?? listedConversation.conversationId ?? ''),
      agentId: current['agentId'] ?? current['agent_id'] ?? listedConversation.agentId,
      status: this.normalizeConversationStatus(current['status'] ?? listedConversation.status)
    } as Conversation;
  }

  private isActiveConversationAssignedToAnotherAgent(conversation: Conversation): boolean {
    if (this.user?.role !== 'agent' || !this.isActiveConversation(conversation)) {
      return false;
    }

    const currentAgentId = String(this.user?.id ?? '').trim();
    const assignedAgentId = String(conversation.agentId ?? '').trim();
    return !!currentAgentId && !!assignedAgentId && assignedAgentId !== currentAgentId;
  }

  private isActiveConversation(conversation: Conversation): boolean {
    return String(conversation.status ?? '').toLowerCase() === ConversationStatus.Active.toLowerCase();
  }

  private normalizeConversationStatus(status: unknown): ConversationStatus {
    switch (String(status ?? '').toLowerCase()) {
      case 'active':
        return ConversationStatus.Active;
      case 'finish':
        return ConversationStatus.Finish;
      case 'pending':
        return ConversationStatus.Pending;
      default:
        return status as ConversationStatus;
    }
  }

  /**
   * Notify parent component about conversation selection
   * This method centralizes the communication logic
   */
  private notifyConversationSelected(conversation: Conversation): void {
    console.log('🔄 Notifying conversation selection:', conversation.conversationId);

    // Method 1: Update service with selected conversation
    this.customerConversationService.setConversation(conversation);

    // The AgentLayoutComponent should be listening to customerConversationService.conversation$
    // to receive the conversation selection changes
  }

  hasNewMessage(conversationId: string): boolean {
    return !!this.chatsWithNewMessage[conversationId];
  }

  filterConversations(): void {
    const query = this.searchQuery.toLowerCase().trim();
    console.log('Filtrando conversaciones con query:', query);
    if (!query) {
      this.filteredConversations = [...this.recentConversation];
      this.sortConversations();
      this.splitConversations();
      return;
    }
    this.filteredConversations = this.recentConversation.filter(chat => {
      const clientMatch = (chat.clientId || '').toLowerCase().includes(query);
      const customMatch = Object.values(chat.customVariables || {})
        .some(val => val?.toLowerCase().includes(query));
      return clientMatch || customMatch;
    });
    this.sortConversations();
    this.splitConversations();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.filteredConversations = [...this.recentConversation];
    this.sortConversations();
  }

  getInitials(name: string): string {
    if (!name) return '?';
    return name.split(' ')
      .map(p => p.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  trackByConversationId(index: number, chat: Conversation): string {
    return chat.conversationId || index.toString();
  }

  get unreadCount(): number {
    return this.recentConversation.filter(c => c.status === ConversationStatus.Pending).length;
  }

  getDisplayName(chat: Conversation): string | null {
    const vars = chat.customVariables || {};
    const name = this.findCustomValue(vars, ['nombres', 'nombre', 'name', 'firstName']);
    const last = this.findCustomValue(vars, ['apellidos', 'apellido', 'lastName']);
    const full = [name, last].filter(Boolean).join(' ');
    return full || null;
  }

  getCustomDetails(chat: Conversation): string | null {
    const vars = chat.customVariables || {};
    const id = this.findCustomValue(vars, ['nit', 'documento', 'id']);
    const comp = this.findCustomValue(vars, ['empresa', 'company']);
    return [id, comp].filter(Boolean).join(' - ') || null;
  }

  getCampaign(chat: Conversation): string | null {
    const vars = chat.customVariables || {};
    return this.findCustomValue(vars, ['campaña', 'campaign', 'campana']);
  }

  /**
   * Obtiene el texto de campaña con información de transferencia si aplica (solo para supervisores)
   */
  getCampaignWithTransfer(chat: Conversation): string | null {
    const currentCampaign = this.getCampaign(chat);

    if (this.isSupervisor && chat.transferData?.isTransfer) {
      let transferToCampaign: string | null = null;

      if (chat.transferHistory && Array.isArray(chat.transferHistory) && chat.transferHistory.length > 0) {
        const lastTransfer = chat.transferHistory[chat.transferHistory.length - 1];
        transferToCampaign = lastTransfer?.toCampaign || null;
      }
      else {
        const transferHistoryStr = chat.customVariables?.['TransferHistory'];
        if (transferHistoryStr) {
          try {
            const historyArray: TransferHistoryItem[] = JSON.parse(transferHistoryStr);
            if (Array.isArray(historyArray) && historyArray.length > 0) {
              const lastTransfer = historyArray[historyArray.length - 1];
              transferToCampaign = lastTransfer?.toCampaign || null;
            }
          } catch (error) {
            console.warn('Error parsing TransferHistory from CustomVariables:', error);
          }
        }
      }

      if (!transferToCampaign && chat.transferData?.transferByCampaign) {
        transferToCampaign = chat.transferData.transferByCampaign;
      }

      // Si tenemos campaña actual (origen) y destino
      if (currentCampaign && transferToCampaign) {
        const result = `${currentCampaign} → ${transferToCampaign}`;
        return result;
      }
      // Si solo tenemos la de destino
      else if (transferToCampaign) {
        const result = `${this.translate.instant('agent.list.noCampaign')} → ${transferToCampaign}`;
        return result;
      }
    }

    return currentCampaign;
  }

  private findCustomValue(vars: Record<string, string>, keys: string[]): string | null {
    for (const [k, v] of Object.entries(vars)) {
      if (v && keys.some(key => k.toLowerCase().includes(key))) {
        return v;
      }
    }
    return null;
  }
}
